/**
 * Light Board (WP3-LIGHTBOARD, BUILD-PLAN M05 wave 3; research library
 * sched-flow.md 4.3). Pulse Status, rebuilt inside Velora: an operatory grid
 * whose room states and signals ("Ready for Dr", "Need assist", "Pt ready")
 * are bound to the real appointment in that chair.
 *
 * Model (sched-flow.md 4.1):
 * - `room_state` is an operatory signal orthogonal to the appointment flow
 *   status. It is Velora-only and never written back to the PMS.
 * - A *signal* is a ping with a lifecycle: raised, claimed ("On my way"),
 *   cleared. `needs_doctor` and `needs_assist` signals are opened and closed
 *   by room-state changes; quick buttons raise the others.
 * - Every write is a *frame* with a client-chosen `frameId`, so the office
 *   gateway (and the browser's own outbox) can queue frames during a WAN
 *   outage and replay them later without duplicates, keeping the original
 *   time (M05 acceptance, SF-18).
 *
 * Minimum necessary (docs/compliance/minimum-necessary.md 3.6): the board
 * carries a patient label of first name plus last initial, or initials only
 * in privacy mode. Full names never leave the server through these routes.
 *
 * Marketplace add-on (M23): every route answers 402 `entitlement_required`
 * without the `lightboard` entitlement for the office.
 */
import { z } from 'zod';

/** Marketplace feature key (entitlement row `feature_key`). */
export const LIGHTBOARD_FEATURE = 'lightboard';

/**
 * Permission keys the module checks. Until dedicated `lightboard.*` keys
 * exist in the identity catalogue (docs/requests/lightboard-permissions.md),
 * watching the board rides on `schedule.view` and office board settings on
 * `settings.manage`. Signalling needs `schedule.view` plus one of
 * `LIGHTBOARD_SIGNAL_ANY`: clinical and front-desk roles hold one, the
 * read-only auditor and billing roles hold neither, so they can watch but
 * never page the office.
 */
export const LIGHTBOARD_PERMISSIONS = {
  view: 'schedule.view',
  signal: 'schedule.view',
  manage: 'settings.manage',
} as const;

/** Write keys that let a board viewer signal (any one of them). */
export const LIGHTBOARD_SIGNAL_ANY: readonly string[] = [
  'schedule.edit',
  'schedule.flow',
  'chart.edit',
];

/** True when this office's permission set may send frames. */
export function canSignalWith(perms: Iterable<string>): boolean {
  const set = new Set(perms);
  return set.has(LIGHTBOARD_PERMISSIONS.signal) && LIGHTBOARD_SIGNAL_ANY.some((k) => set.has(k));
}

/* ------------------------------------------------------------------------ */
/* Vocabulary                                                                */
/* ------------------------------------------------------------------------ */

export const ROOM_STATES = [
  'empty',
  'seated',
  'needs_doctor',
  'doctor_in',
  'needs_assist',
  'turnover',
  'clean',
] as const;
export const RoomStateSchema = z.enum(ROOM_STATES);
export type RoomState = z.infer<typeof RoomStateSchema>;

export const ROOM_STATE_LABELS: Readonly<Record<RoomState, string>> = {
  empty: 'Open',
  seated: 'Seated',
  needs_doctor: 'Needs doctor',
  doctor_in: 'Doctor in',
  needs_assist: 'Needs assist',
  turnover: 'Turnover',
  clean: 'Room clean',
};

export const DOCTOR_REASONS = [
  'hyg_exam',
  'anesthetic',
  'consult',
  'question',
  'emergency',
] as const;
export const DoctorReasonSchema = z.enum(DOCTOR_REASONS);
export type DoctorReason = z.infer<typeof DoctorReasonSchema>;

export const DOCTOR_REASON_LABELS: Readonly<Record<DoctorReason, string>> = {
  hyg_exam: 'Hygiene exam',
  anesthetic: 'Anesthetic',
  consult: 'Consult',
  question: 'Question',
  emergency: 'Emergency',
};

/** Pulse Status priorities, lowest first. */
export const PRIORITIES = ['low', 'normal', 'high', 'urgent', 'emergency'] as const;
export const PrioritySchema = z.enum(PRIORITIES);
export type Priority = z.infer<typeof PrioritySchema>;

export const SIGNAL_KINDS = [
  'needs_doctor',
  'needs_assist',
  'pt_ready',
  'xray',
  'checkout_ready',
  'billing',
  'doorbell',
  'phone',
  'urgent',
  'emergency',
  'custom',
] as const;
export const SignalKindSchema = z.enum(SIGNAL_KINDS);
export type SignalKind = z.infer<typeof SignalKindSchema>;

/** Kinds that only exist while a room is in the matching state. */
export const ROOM_BOUND_KINDS: readonly SignalKind[] = ['needs_doctor', 'needs_assist'];

/** Kinds that page the whole office and never belong to one room. */
export const OFFICE_KINDS: readonly SignalKind[] = ['doorbell', 'urgent', 'emergency'];

export const CLEAR_REASONS = ['cleared', 'doctor_in', 'state_changed', 'expired'] as const;
export const ClearReasonSchema = z.enum(CLEAR_REASONS);
export type ClearReason = z.infer<typeof ClearReasonSchema>;

/** Where a frame came from (frame log `source`). */
export const FRAME_SOURCES = ['board', 'chair', 'gateway', 'replay', 'api'] as const;
export const FrameSourceSchema = z.enum(FRAME_SOURCES);
export type FrameSource = z.infer<typeof FrameSourceSchema>;

/* ------------------------------------------------------------------------ */
/* Quick buttons                                                             */
/* ------------------------------------------------------------------------ */

/** What pressing a quick button does. */
export const QuickActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('state'),
    state: RoomStateSchema,
    reason: DoctorReasonSchema.optional(),
  }),
  z.object({
    type: z.literal('signal'),
    kind: SignalKindSchema,
    priority: PrioritySchema,
  }),
]);
export type QuickAction = z.infer<typeof QuickActionSchema>;

export const ButtonIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]{1,39}$/, 'button id is kebab-case, 2 to 40 characters');

/** Short, configured text. Never free text typed at signal time (no PHI). */
export const ButtonLabelSchema = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .regex(/^[\p{L}\p{N} .,'&/()+:-]+$/u, 'labels use letters, digits and simple punctuation');

export const QuickButtonSchema = z.object({
  id: ButtonIdSchema,
  label: ButtonLabelSchema,
  /** One-line hint under the label. */
  detail: z.string().trim().max(48).default(''),
  /** Lucide icon name (kebab-case). Never an emoji. */
  icon: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  action: QuickActionSchema,
  /** `room` needs a selected operatory; `office` pages everyone. */
  scope: z.enum(['room', 'office']),
  /** Urgent and Emergency can never be hidden (Pulse Status PROTECTED_IDS). */
  protected: z.boolean().default(false),
  custom: z.boolean().default(false),
});
export type QuickButton = z.infer<typeof QuickButtonSchema>;

/**
 * The twelve Pulse Status defaults, in order, adapted to rooms. Room-state
 * buttons drive `room_state`; the rest raise a signal.
 */
export const DEFAULT_QUICK_BUTTONS: readonly QuickButton[] = [
  {
    id: 'pt-ready',
    label: 'Pt ready',
    detail: 'Patient ready to come back',
    icon: 'user-check',
    action: { type: 'signal', kind: 'pt_ready', priority: 'normal' },
    scope: 'room',
    protected: false,
    custom: false,
  },
  {
    id: 'ready-for-dr',
    label: 'Ready for Dr',
    detail: 'Hygiene exam',
    icon: 'stethoscope',
    action: { type: 'state', state: 'needs_doctor', reason: 'hyg_exam' },
    scope: 'room',
    protected: false,
    custom: false,
  },
  {
    id: 'need-assist',
    label: 'Need assist',
    detail: 'Second pair of hands',
    icon: 'hand',
    action: { type: 'state', state: 'needs_assist' },
    scope: 'room',
    protected: false,
    custom: false,
  },
  {
    id: 'xray',
    label: 'X-ray',
    detail: 'Help with images',
    icon: 'scan-line',
    action: { type: 'signal', kind: 'xray', priority: 'low' },
    scope: 'room',
    protected: false,
    custom: false,
  },
  {
    id: 'room-clean',
    label: 'Room clean',
    detail: 'Ready for the next patient',
    icon: 'sparkles',
    action: { type: 'state', state: 'clean' },
    scope: 'room',
    protected: false,
    custom: false,
  },
  {
    id: 'room-dirty',
    label: 'Room dirty',
    detail: 'Needs turnover',
    icon: 'spray-can',
    action: { type: 'state', state: 'turnover' },
    scope: 'room',
    protected: false,
    custom: false,
  },
  {
    id: 'checkout',
    label: 'Checkout',
    detail: 'Patient heading to the desk',
    icon: 'log-out',
    action: { type: 'signal', kind: 'checkout_ready', priority: 'normal' },
    scope: 'room',
    protected: false,
    custom: false,
  },
  {
    id: 'billing',
    label: 'Billing',
    detail: 'Desk needed in the room',
    icon: 'receipt',
    action: { type: 'signal', kind: 'billing', priority: 'normal' },
    scope: 'room',
    protected: false,
    custom: false,
  },
  {
    id: 'doorbell',
    label: 'Doorbell',
    detail: 'Someone at the door',
    icon: 'bell-ring',
    action: { type: 'signal', kind: 'doorbell', priority: 'normal' },
    scope: 'office',
    protected: false,
    custom: false,
  },
  {
    id: 'phone-for-dr',
    label: 'Phone for Dr',
    detail: 'Call holding',
    icon: 'phone',
    action: { type: 'signal', kind: 'phone', priority: 'high' },
    scope: 'room',
    protected: false,
    custom: false,
  },
  {
    id: 'urgent',
    label: 'Urgent',
    detail: 'Come now',
    icon: 'triangle-alert',
    action: { type: 'signal', kind: 'urgent', priority: 'urgent' },
    scope: 'office',
    protected: true,
    custom: false,
  },
  {
    id: 'emergency',
    label: 'Emergency',
    detail: 'Office-wide page',
    icon: 'siren',
    action: { type: 'signal', kind: 'emergency', priority: 'emergency' },
    scope: 'office',
    protected: true,
    custom: false,
  },
];

export const PROTECTED_BUTTON_IDS: readonly string[] = DEFAULT_QUICK_BUTTONS.filter(
  (b) => b.protected,
).map((b) => b.id);

/** A custom button an office adds. Custom buttons only raise signals. */
export const CustomButtonSchema = z.object({
  id: ButtonIdSchema,
  label: ButtonLabelSchema,
  detail: z.string().trim().max(48).default(''),
  priority: PrioritySchema.exclude(['emergency']).default('normal'),
  scope: z.enum(['room', 'office']).default('room'),
});
export type CustomButton = z.infer<typeof CustomButtonSchema>;

/* ------------------------------------------------------------------------ */
/* Office settings                                                           */
/* ------------------------------------------------------------------------ */

/** Pulse Status ageing buckets: fresh, warning, delayed, critical (3/7/12 min). */
export const AgeingSchema = z
  .object({
    warnMin: z.number().int().min(1).max(120),
    delayedMin: z.number().int().min(1).max(120),
    criticalMin: z.number().int().min(1).max(240),
  })
  .refine((a) => a.warnMin < a.delayedMin && a.delayedMin < a.criticalMin, {
    message: 'thresholds must increase: warning < delayed < critical',
  });
export type Ageing = z.infer<typeof AgeingSchema>;

export const LightboardSettingsSchema = z.object({
  /** Unclaimed signals escalate to everyone after this long (SF-10, default 7). */
  escalateAfterMin: z.number().int().min(1).max(60),
  /** Low and normal signals nobody claimed drop off after this long. */
  expireAfterMin: z.number().int().min(5).max(240),
  ageing: AgeingSchema,
  /** What the board shows for a patient. Initials suits waiting-room screens. */
  patientLabel: z.enum(['first_initial', 'initials']),
  /** Defaults this office hides (protected ids are refused). */
  hiddenButtons: z.array(ButtonIdSchema).max(20),
  customButtons: z.array(CustomButtonSchema).max(12),
  /** Show room states and signals on the Schedule flow board. */
  showOnFlowBoard: z.boolean(),
  /** Play the Emergency page over Radio room speakers (needs the Radio add-on). */
  emergencyOverRadio: z.boolean(),
});
export type LightboardSettings = z.infer<typeof LightboardSettingsSchema>;

export const DEFAULT_LIGHTBOARD_SETTINGS: LightboardSettings = {
  escalateAfterMin: 7,
  expireAfterMin: 30,
  ageing: { warnMin: 3, delayedMin: 7, criticalMin: 12 },
  patientLabel: 'first_initial',
  hiddenButtons: [],
  customButtons: [],
  showOnFlowBoard: true,
  emergencyOverRadio: false,
};

export const LightboardSettingsPatchSchema = z
  .object({
    escalateAfterMin: LightboardSettingsSchema.shape.escalateAfterMin.optional(),
    expireAfterMin: LightboardSettingsSchema.shape.expireAfterMin.optional(),
    ageing: AgeingSchema.optional(),
    patientLabel: LightboardSettingsSchema.shape.patientLabel.optional(),
    hiddenButtons: LightboardSettingsSchema.shape.hiddenButtons.optional(),
    customButtons: LightboardSettingsSchema.shape.customButtons.optional(),
    showOnFlowBoard: z.boolean().optional(),
    emergencyOverRadio: z.boolean().optional(),
  })
  .strict();
export type LightboardSettingsPatch = z.infer<typeof LightboardSettingsPatchSchema>;

/* ------------------------------------------------------------------------ */
/* Frames (writes)                                                           */
/* ------------------------------------------------------------------------ */

const FrameBase = z.object({
  /** Client-chosen id; replays of the same frame are ignored (idempotent). */
  frameId: z.uuid(),
  /**
   * When the action happened. Honoured only for replayed frames (gateway or
   * browser outbox) and clamped to the last 24 hours; live frames are
   * server-stamped (SF-04).
   */
  occurredAt: z.iso.datetime({ offset: true }).optional(),
});

export const RoomStateFrameSchema = FrameBase.extend({
  type: z.literal('room.state'),
  operatoryId: z.uuid(),
  state: RoomStateSchema,
  reason: DoctorReasonSchema.optional(),
  /** Optional explicit binding; otherwise the server binds the chair's appointment. */
  appointmentId: z.uuid().optional(),
});

export const SignalRaiseFrameSchema = FrameBase.extend({
  type: z.literal('signal.raise'),
  /** Client-chosen so a queued claim or clear can refer to it before replay. */
  signalId: z.uuid().optional(),
  operatoryId: z.uuid().nullable(),
  kind: SignalKindSchema.exclude(['needs_doctor', 'needs_assist']),
  /** Default or custom quick button pressed; its label is used. */
  buttonId: ButtonIdSchema,
  appointmentId: z.uuid().optional(),
});

export const SignalClaimFrameSchema = FrameBase.extend({
  type: z.literal('signal.claim'),
  signalId: z.uuid(),
});

export const SignalClearFrameSchema = FrameBase.extend({
  type: z.literal('signal.clear'),
  signalId: z.uuid(),
});

export const LightboardFrameSchema = z.discriminatedUnion('type', [
  RoomStateFrameSchema,
  SignalRaiseFrameSchema,
  SignalClaimFrameSchema,
  SignalClearFrameSchema,
]);
export type LightboardFrame = z.infer<typeof LightboardFrameSchema>;
export type FrameType = LightboardFrame['type'];

/** Gateway catch-up: the gateway keeps up to 5,000 frames (M05) and sends them in batches. */
export const FRAME_BATCH_MAX = 500;
export const GATEWAY_QUEUE_CAPACITY = 5000;
/** Replayed frames older than this are refused (a stale queue must not rewrite the past). */
export const REPLAY_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const FrameBatchSchema = z.object({
  source: z.enum(['gateway', 'replay']),
  frames: z.array(LightboardFrameSchema).min(1).max(FRAME_BATCH_MAX),
});
export type FrameBatch = z.infer<typeof FrameBatchSchema>;

export const FRAME_OUTCOMES = ['applied', 'duplicate', 'rejected'] as const;
export const FrameOutcomeSchema = z.enum(FRAME_OUTCOMES);

export const FrameResultSchema = z.object({
  frameId: z.uuid(),
  outcome: FrameOutcomeSchema,
  /** Machine-readable reason when rejected (`not_found`, `already_claimed`, `too_old`...). */
  code: z.string().nullable(),
  message: z.string().nullable(),
  signalId: z.uuid().nullable(),
  operatoryId: z.uuid().nullable(),
});
export type FrameResult = z.infer<typeof FrameResultSchema>;

export const FrameBatchResultSchema = z.object({
  results: z.array(FrameResultSchema),
  applied: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
});
export type FrameBatchResult = z.infer<typeof FrameBatchResultSchema>;

/* ------------------------------------------------------------------------ */
/* Board (reads)                                                             */
/* ------------------------------------------------------------------------ */

export const PersonRefSchema = z.object({ id: z.uuid(), name: z.string() });
export type PersonRef = z.infer<typeof PersonRefSchema>;

export const ProviderRefSchema = z.object({
  id: z.uuid(),
  abbr: z.string(),
  /** "Dr. Smith" style; no first names needed on a wall. */
  name: z.string(),
  kind: z.enum(['dentist', 'hygienist', 'assistant', 'other']),
});
export type ProviderRef = z.infer<typeof ProviderRefSchema>;

export const FlowStatusLiteSchema = z.enum([
  'scheduled',
  'late',
  'here',
  'ready',
  'chair',
  'checkout',
  'complete',
]);

/** The appointment a room or signal is bound to, minimum necessary. */
export const BoundAppointmentSchema = z.object({
  id: z.uuid(),
  /** "Jenna T." or "J. T." (privacy); never a full name. */
  patientLabel: z.string(),
  provider: ProviderRefSchema.nullable(),
  flowStatus: FlowStatusLiteSchema,
  startsAt: z.iso.datetime({ offset: true }),
  lengthMin: z.number().int().positive(),
  arrivedAt: z.iso.datetime({ offset: true }).nullable(),
  seatedAt: z.iso.datetime({ offset: true }).nullable(),
});
export type BoundAppointment = z.infer<typeof BoundAppointmentSchema>;

export const SignalSchema = z.object({
  id: z.uuid(),
  locationId: z.uuid(),
  operatoryId: z.uuid().nullable(),
  /** Room name for display, null for office-wide pages. */
  operatoryName: z.string().nullable(),
  kind: SignalKindSchema,
  label: z.string(),
  priority: PrioritySchema,
  reason: DoctorReasonSchema.nullable(),
  appointment: BoundAppointmentSchema.nullable(),
  raisedAt: z.iso.datetime({ offset: true }),
  raisedBy: PersonRefSchema,
  claimedAt: z.iso.datetime({ offset: true }).nullable(),
  claimedBy: PersonRefSchema.nullable(),
  /** Unclaimed past the office threshold: shown to everyone, red (SF-10). */
  escalated: z.boolean(),
  /** Doctor queue: 1 = next in line for that provider. Null when claimed or not queued. */
  queuePosition: z.number().int().positive().nullable(),
  /** Who the doctor queue is for ("Dr. Smith"), null when any doctor. */
  queueFor: ProviderRefSchema.nullable(),
  /**
   * Ready for Dr from a hygienist: the clinical summary (deeper sites, BOP,
   * x-rays today, findings, planned perio therapy). Only for viewers with
   * chart.view and never on an initials-only device. Absent from older servers.
   */
  detail: z.string().max(600).nullable().optional(),
});
export type Signal = z.infer<typeof SignalSchema>;

export const RoomSchema = z.object({
  operatoryId: z.uuid(),
  name: z.string(),
  abbr: z.string(),
  isHygiene: z.boolean(),
  provider: ProviderRefSchema.nullable(),
  state: RoomStateSchema,
  /** When the room entered `state`; null when derived (nobody set it today). */
  stateSince: z.iso.datetime({ offset: true }).nullable(),
  stateSetBy: PersonRefSchema.nullable(),
  reason: DoctorReasonSchema.nullable(),
  appointment: BoundAppointmentSchema.nullable(),
  /** Next appointment start today in this room, for "Open, next 10:30". */
  nextStartsAt: z.iso.datetime({ offset: true }).nullable(),
  signals: z.array(SignalSchema),
});
export type Room = z.infer<typeof RoomSchema>;

export const PresenceSchema = z.object({
  userId: z.uuid(),
  name: z.string(),
  operatoryId: z.uuid().nullable(),
  lastSeenAt: z.iso.datetime({ offset: true }),
});
export type Presence = z.infer<typeof PresenceSchema>;

/**
 * Additive day numbers so "All offices" is a ratio of sums (ADR-0012):
 * average doctor wait = doctorWaitSeconds / doctorWaitCount.
 */
export const BoardStatsSchema = z.object({
  activeSignals: z.number().int().nonnegative(),
  needsDoctor: z.number().int().nonnegative(),
  escalated: z.number().int().nonnegative(),
  roomsInUse: z.number().int().nonnegative(),
  rooms: z.number().int().nonnegative(),
  doctorWaitSeconds: z.number().int().nonnegative(),
  doctorWaitCount: z.number().int().nonnegative(),
  signalsToday: z.number().int().nonnegative(),
});
export type BoardStats = z.infer<typeof BoardStatsSchema>;

export const OfficeBoardSchema = z.object({
  locationId: z.uuid(),
  name: z.string(),
  shortName: z.string(),
  colorToken: z.string(),
  timezone: z.string(),
  /** Read-only grace (lapsed entitlement): board shows, writes refused. */
  readOnly: z.boolean(),
  /**
   * Whether the office's PMS sends patient flow (seated, checkout). When it
   * does not (PulseSync v1), room tiles show only what staff set here, so
   * the board says "Flow not synced" instead of a wall of "Open" rooms.
   */
  flow: z
    .object({
      state: z.enum(['velora', 'synced', 'not_synced']),
      reason: z.string().nullable(),
    })
    .optional(),
  settings: LightboardSettingsSchema,
  buttons: z.array(QuickButtonSchema),
  rooms: z.array(RoomSchema),
  /** Office-wide pages (doorbell, urgent, emergency) and room-less signals. */
  officeSignals: z.array(SignalSchema),
  presence: z.array(PresenceSchema),
  stats: BoardStatsSchema,
});
export type OfficeBoard = z.infer<typeof OfficeBoardSchema>;

export const BoardQuerySchema = z.object({
  /** `initials` forces initials for this device (privacy mode, wall screens; SF-15). */
  names: z.enum(['default', 'initials']).default('default'),
});
export type BoardQuery = z.infer<typeof BoardQuerySchema>;

export const LightboardBoardSchema = z.object({
  /** Server clock; clients render timers with the measured offset (SF-04). */
  serverNow: z.iso.datetime({ offset: true }),
  offices: z.array(OfficeBoardSchema),
  /** Sum of every office's stats (ratio of sums for averages). */
  combined: BoardStatsSchema,
});
export type LightboardBoard = z.infer<typeof LightboardBoardSchema>;

/* ------------------------------------------------------------------------ */
/* History                                                                   */
/* ------------------------------------------------------------------------ */

export const HistoryQuerySchema = z.object({
  date: z.iso.date().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

export const HistoryEntrySchema = z.object({
  id: z.uuid(),
  frameType: z.enum(['room.state', 'signal.raise', 'signal.claim', 'signal.clear']),
  operatoryId: z.uuid().nullable(),
  operatoryName: z.string().nullable(),
  signalId: z.uuid().nullable(),
  /** Signal label or room state label. */
  label: z.string(),
  state: RoomStateSchema.nullable(),
  kind: SignalKindSchema.nullable(),
  priority: PrioritySchema.nullable(),
  actor: PersonRefSchema.nullable(),
  source: FrameSourceSchema,
  occurredAt: z.iso.datetime({ offset: true }),
  receivedAt: z.iso.datetime({ offset: true }),
  /** For claims: seconds from raise to claim. */
  responseSeconds: z.number().int().nonnegative().nullable(),
});
export type HistoryEntry = z.infer<typeof HistoryEntrySchema>;

export const LightboardHistorySchema = z.object({
  date: z.iso.date(),
  entries: z.array(HistoryEntrySchema),
});
export type LightboardHistory = z.infer<typeof LightboardHistorySchema>;

/* ------------------------------------------------------------------------ */
/* Presence                                                                  */
/* ------------------------------------------------------------------------ */

export const PresenceHeartbeatSchema = z.object({
  /** The room this screen sits in (a chair tablet), or null for a desk or wall. */
  operatoryId: z.uuid().nullable(),
});

/** Heartbeats every 30 s; an entry disappears after this long without one. */
export const PRESENCE_TTL_MS = 75_000;

/* ------------------------------------------------------------------------ */
/* Marketplace listing (consumed by WP4-MARKET)                              */
/* ------------------------------------------------------------------------ */

/** What the Marketplace card and detail page say about this add-on (M23 manifest). */
export const LIGHTBOARD_LISTING = {
  key: LIGHTBOARD_FEATURE,
  name: 'Light Board',
  origin: 'Pulse Status',
  category: 'Run the office',
  priceCents: 3900,
  trialDays: 14,
  valueLine: 'Pt ready in Op 3, seen by everyone in one second, with the clock running.',
  pitch:
    'A light board for every operatory: patient seated, doctor needed, room turnover, bound to the real appointment.',
  features: [
    'Room states and Ready for Dr with a doctor queue and On my way claims',
    'Quick buttons, office-wide pages and escalation after 7 minutes',
    'Keeps working through a WAN outage and replays when the line is back',
  ],
  dataAccess: {
    reads: ['appointments (today)', 'operatories', 'providers', 'staff presence'],
    writes: ['Light Board room states and signals (Velora only)'],
    pmsWriteBack: false,
    phiEgress: false,
  },
} as const;

/** SSE entity keys the module publishes (ids only). */
export const LIGHTBOARD_ENTITY = {
  room: 'lightboard.room',
  signal: 'lightboard.signal',
  settings: 'lightboard.settings',
  presence: 'lightboard.presence',
} as const;
